import React, { useMemo } from 'react';
import { Avatar, ConfigProvider } from 'antd';
import type { AvatarProps as AntAvatarProps } from 'antd';
import * as Icons from '@ant-design/icons';
import { useAndStore } from '../../base/store/useAndStore';
import { getDataSetStoreKey } from '../../base/storeKeyUtils';
import type { BaseComponentProps, DataSetConfig } from '../../types/common';

/**
 * Avatar Design Token
 * 参考 Ant Design 6.x Design Token 规范
 */
export interface AvatarToken {
  /** 头像尺寸 */
  containerSize?: number;
  containerSizeLG?: number;
  containerSizeSM?: number;
  /** 文字尺寸 */
  textFontSize?: number;
  textFontSizeLG?: number;
  textFontSizeSM?: number;
  /** 组头像间距 */
  groupOverlapping?: number;
  groupBorderColor?: string;
}

export interface AndAvatarProps extends AntAvatarProps, BaseComponentProps, DataSetConfig {
  clickAction?: string;
  icon?: React.ReactNode | string;
  /** Design Token */
  token?: AvatarToken;
}

export const AndAvatar: React.FC<AndAvatarProps> = ({
  id, sessionId, develop, children, dataSet, dataSetField, src: propSrc, clickAction, icon, token, ...restProps
}) => {
  const getDataSet = useAndStore((state) => state.getDataSet);
  const getProps = useAndStore((state) => state.getProps);
  const dataSetKey = useMemo(
    () => getDataSetStoreKey(sessionId, dataSet),
    [sessionId, dataSet]
  );

  const storeProps = useMemo(() => {
    if (sessionId && id) return getProps(sessionId, id);
    return null;
  }, [sessionId, id, getProps]);

  const storeValue = useMemo(() => {
    if (dataSetKey && dataSetField) return getDataSet(dataSetKey, dataSetField);
    return undefined;
  }, [dataSetKey, dataSetField, getDataSet]);

  // Avatar 主要绑定 src
  const computedSrc = useMemo(() => {
    let finalSrc = propSrc;
    if (!develop) {
      if (storeValue !== undefined && storeValue !== null && storeValue !== '') {
        finalSrc = String(storeValue);
      } else if (storeProps?.src !== undefined) {
        finalSrc = storeProps.src;
      }
    }
    // Fix: return undefined if empty string to avoid warning
    if (typeof finalSrc === 'string' && finalSrc.trim() === '') {
      return undefined;
    }
    return finalSrc;
  }, [develop, storeValue, storeProps?.src, propSrc]);

  // Handle Icon: Resolve string to Component
  const computedIcon = useMemo(() => {
    let finalIcon = icon;
    if (!develop && storeProps?.icon) {
      finalIcon = storeProps.icon as any;
    }

    if (typeof finalIcon === 'string' && finalIcon) {
      const IconComp = (Icons as any)[finalIcon];
      if (IconComp) return <IconComp />;
    }
    return finalIcon as React.ReactNode;
  }, [icon, storeProps?.icon, develop]);

  const mergedProps = useMemo(() => {
    const baseProps = { ...restProps };
    if (storeProps && typeof storeProps === 'object') {
      Object.keys(storeProps).forEach((key) => {
        // Exclude handled props
        if (!['src', 'icon', 'token'].includes(key) && storeProps[key] !== undefined && storeProps[key] !== null) {
          (baseProps as any)[key] = storeProps[key];
        }
      });
    }
    return baseProps;
  }, [restProps, storeProps]);

  // 过滤和转换 token (处理字符串转数字)
  const filteredToken = useMemo(() => {
    const storeToken = storeProps?.token as AvatarToken | undefined;
    const combinedToken = { ...token, ...storeToken };
    if (!combinedToken || Object.keys(combinedToken).length === 0) return undefined;
    
    const result: Record<string, any> = {};
    Object.entries(combinedToken).forEach(([key, value]) => {
      if (value !== '' && value !== undefined && value !== null) {
        // 数字类型的 token 需要转换
        if (['containerSize', 'containerSizeLG', 'containerSizeSM', 
             'textFontSize', 'textFontSizeLG', 'textFontSizeSM', 
             'groupOverlapping'].includes(key)) {
          const numVal = typeof value === 'string' ? parseInt(value, 10) : value;
          if (!isNaN(numVal)) {
            result[key] = numVal;
          }
        } else {
          result[key] = value;
        }
      }
    });
    return Object.keys(result).length > 0 ? result : undefined;
  }, [token, storeProps?.token]);

  // 构建 theme 配置
  const themeConfig = useMemo(() => {
    if (!filteredToken) return undefined;
    return { components: { Avatar: filteredToken } };
  }, [filteredToken]);

  const avatarElement = (
    <Avatar 
      {...mergedProps} 
      src={computedSrc} 
      icon={computedIcon}
    >
      {children}
    </Avatar>
  );

  if (themeConfig) {
    return <ConfigProvider theme={themeConfig}>{avatarElement}</ConfigProvider>;
  }

  return avatarElement;
};

export default AndAvatar;
